import { lazy, Suspense, useEffect, useState } from "react";
import MainContainer from "../../components/MainContainer";

import {
  AnnouncementsButton,
  AnnouncementsButtonContainer,
  AnnouncementsPopup,
  BackButton,
  Counter,
  Line,
  PageTitle,
  PopupButton,
  PopupText,
  PopupTitle,
  PopupTitleContainer,
  ReferralRewardsContainer,
  TitleContainer,
  PopupOverlay,
} from "./SpinWheelPage.styles";
import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as AnnouncementsIcon } from "../../assets/icons/announcements.svg";
import { useNavigate } from "react-router-dom";
import {
  //   CollectButton,
  CopyButton,
  ProgressBar,
  ProgressContainer,
  ProgressFill,
  ProgressText,
  ReferralLink,
  ReferralLinkContainer,
  SectionContent,
  SectionDescriptionFirst,
  SectionDescriptionSecond,
  SectionTitle,
  StyledRow,
} from "./SpinWheelPage.styles";
import { MAX_USAGE_AFFILIATE } from "../../utils/constants";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import { ReactComponent as CopySvg } from "../../assets/icons/copy.svg";
import { ReactComponent as SuccessSvg } from "../../assets/icons/success.svg";
import { toast } from "../../components/modern-ui/sonner";
import Loader from "../../components/utils/Loader/Loader";

const SpinWheel = lazy(() => import("../../components/SpinWheel/SpinWheel"));

const SpinWheelPage = () => {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [isShowPopup, setIsShowPopup] = useState(false);
  const [isPopupAnimating, setIsPopupAnimating] = useState(false);
  const user = useSelector(selectUser);
  const referralLink = user?.affiliateLink;

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const prevRootOverflowX = root.style.overflowX;
    const prevBodyOverflowX = body.style.overflowX;
    root.style["overflow-x"] = "hidden";
    body.style["overflow-x"] = "hidden";
    return () => {
      root.style.overflowX = prevRootOverflowX;
      body.style.overflowX = prevBodyOverflowX;
    };
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const openPopup = () => {
    setIsShowPopup(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsPopupAnimating(true);
      });
    });
  };

  const closePopup = () => {
    setIsPopupAnimating(false);
    setTimeout(() => {
      setIsShowPopup(false);
    }, 200);
  };

  const togglePopup = () => {
    if (isShowPopup) {
      closePopup();
    } else {
      openPopup();
    }
  };

  useEffect(() => {
    if (!user?.lastFreeCredits) {
      setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const calculateRemainingTime = () => {
      const now = new Date();
      const lastClaim = new Date(user?.lastFreeCredits);
      const nextClaim = new Date(lastClaim.getTime() + 24 * 60 * 60 * 1000); // 24 hours later

      const timeDiff = nextClaim.getTime() - now.getTime();

      if (timeDiff <= 0) {
        // 24 hours have passed, can claim again
        return { hours: 0, minutes: 0, seconds: 0 };
      }

      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    // Set initial time
    setTimeRemaining(calculateRemainingTime());

    // Update every second
    const interval = setInterval(() => {
      const remaining = calculateRemainingTime();
      setTimeRemaining(remaining);

      // If time reached 0, user can claim again
      if (
        remaining.hours === 0 &&
        remaining.minutes === 0 &&
        remaining.seconds === 0
      ) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [user?.lastFreeCredits]);

  const formatTime = (time) => String(time).padStart(2, "0");

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error("Failed to copy link. Please try again.");
    }
  };

  //   const handleShare = async () => {
  //     const shareData = {
  //       title: "Join Playbun - NSFW AI Video Generation",
  //       text: "Create amazing NSFW AI videos with Playbun! Use my referral link to get started.",
  //       url: referralLink,
  //     };

  //     try {
  //       // Check if Web Share API is supported (mainly on mobile)
  //       if (navigator.share) {
  //         await navigator.share(shareData);
  //         toast.success("Invite shared successfully!");
  //       } else {
  //         // Fallback: Copy to clipboard if Web Share API is not supported
  //         await navigator.clipboard.writeText(
  //           `${shareData.text} ${shareData.url}`
  //         );
  //         toast.success("Invite message copied to clipboard!");
  //       }
  //     } catch (err) {
  //       if (err.name !== "AbortError") {
  //         // User didn't cancel the share
  //         console.error("Error sharing:", err);
  //         toast.error("Failed to share invite. Please try again.");
  //       }
  //     }
  //   };

  return (
    <MainContainer hideTabs={true} hideOverflowX>
      <TitleContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back
        </BackButton>

        <AnnouncementsButtonContainer>
          <AnnouncementsButton onClick={togglePopup}>
            <AnnouncementsIcon />
          </AnnouncementsButton>

          {isShowPopup && (
            <>
              <PopupOverlay onClick={closePopup} />
              <AnnouncementsPopup $isVisible={isPopupAnimating}>
                <PopupTitleContainer>
                  <PopupTitle>Giveaways and Announcements</PopupTitle>
                  <CloseIcon
                    style={{ cursor: "pointer" }}
                    onClick={closePopup}
                  />
                </PopupTitleContainer>
                <PopupText>Join Telegram Channel</PopupText>
                <PopupButton
                  onClick={() => {
                    window.open("https://t.me/Playboxvip", "_blank");
                  }}
                >
                  t.me/Playboxvip
                </PopupButton>
              </AnnouncementsPopup>
            </>
          )}
        </AnnouncementsButtonContainer>
      </TitleContainer>
      <TitleContainer $offMarginTop>
        <PageTitle>Free Credits</PageTitle>
        {user?.lastFreeCredits &&
          !(
            timeRemaining.hours === 0 &&
            timeRemaining.minutes === 0 &&
            timeRemaining.seconds === 0
          ) && (
            <Counter>
              FREE SPIN IN {formatTime(timeRemaining.hours)}:
              {formatTime(timeRemaining.minutes)}:
              {formatTime(timeRemaining.seconds)}
            </Counter>
          )}
      </TitleContainer>

      <Suspense fallback={<Loader text="Loading spin wheel" />}>
        <SpinWheel
          isFreeSpin={
            timeRemaining.hours === 0 &&
            timeRemaining.minutes === 0 &&
            timeRemaining.seconds === 0
          }
        />

        <Line />
        <ReferralRewardsContainer>
          <SectionContent>
            <SectionTitle>Referral Rewards</SectionTitle>
            <SectionDescriptionFirst>
              Earn 5 credits for each friend who signs up!
            </SectionDescriptionFirst>
            {user?.usageOfAffiliate >= MAX_USAGE_AFFILIATE && (
              <SectionDescriptionSecond>
                (Only the first 400 referrals grant you free credits. Affiliate
                earnings from referral purchases are uncapped.)
              </SectionDescriptionSecond>
            )}

            <ReferralLinkContainer>
              <ReferralLink onClick={handleCopyLink}>
                <span>{referralLink?.replace("https://www.", "")}</span>
              </ReferralLink>
              <CopyButton onClick={handleCopyLink} $success={copySuccess}>
                {copySuccess ? <SuccessSvg /> : <CopySvg />}
              </CopyButton>
            </ReferralLinkContainer>
            <StyledRow>
              {/* <CollectButton $invite onClick={handleShare}>
              Invite Friends
            </CollectButton> */}

              <ProgressContainer>
                <ProgressBar>
                  <ProgressFill
                    $progress={
                      (Math.min(user?.usageOfAffiliate, 400) /
                        MAX_USAGE_AFFILIATE) *
                      100
                    }
                  />
                </ProgressBar>
                <ProgressText>
                  {user?.usageOfAffiliate} friends invited
                </ProgressText>
              </ProgressContainer>
            </StyledRow>
          </SectionContent>
        </ReferralRewardsContainer>
      </Suspense>
    </MainContainer>
  );
};

export default SpinWheelPage;
